import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { Building02Icon } from '@hugeicons/core-free-icons'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@backend/_generated/api'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/onboarding/_blank/')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const createOrganization = useMutation(api.organization.createOrganization)
  const updateMyName = useMutation(api.profile.updateMyName)
  const profile = useQuery(api.profile.getMyProfile)

  const form = useForm({
    defaultValues: {
      yourName: '',
      name: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const yourName = value.yourName.trim()
        if (!yourName) {
          toast.error('Your name is required')
          return
        }
        if (!value.name.trim()) {
          toast.error('Organization name is required')
          return
        }

        // Save the user's name
        await updateMyName({ name: yourName })

        // Create organization via Convex
        const { organizationId } = await createOrganization({
          name: value.name.trim(),
        })

        // Store for position assignment step
        localStorage.setItem('pendingOrganizationId', organizationId)

        toast.success(
          `Organization "${value.name.trim()}" created successfully!`,
        )
        form.reset()

        // Navigate to position assignment
        navigate({
          to: '/onboarding/assign-position',
          search: { orgId: organizationId },
        })
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to create organization',
        )
      }
    },
  })

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <HugeiconsIcon
              icon={Building02Icon}
              className="h-10 w-10 text-primary"
            />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Welcome to NextPeak
          </h1>
          <p className="text-muted-foreground text-base">
            Create your organization to get started
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>Get Started</CardTitle>
            <CardDescription>
              Tell us your name and create your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
              className="space-y-4"
            >
              <form.Field
                name="yourName"
                defaultValue={profile?.name ?? ''}
                validators={{
                  onChange: ({ value }) =>
                    !value || value.trim().length === 0
                      ? 'Your name is required'
                      : value.trim().length < 2
                        ? 'Name must be at least 2 characters'
                        : undefined,
                }}
              >
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="yourName">Your Name</Label>
                    <Input
                      id="yourName"
                      placeholder="Jane Smith"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={form.state.isSubmitting}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) =>
                    !value || value.trim().length === 0
                      ? 'Organization name is required'
                      : value.trim().length < 2
                        ? 'Organization name must be at least 2 characters'
                        : undefined,
                }}
              >
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="organizationName">Organization Name</Label>
                    <Input
                      id="organizationName"
                      placeholder="Acme Inc."
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={form.state.isSubmitting}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <Button
                type="submit"
                className="w-full"
                disabled={form.state.isSubmitting}
              >
                {form.state.isSubmitting
                  ? 'Creating Organization...'
                  : 'Create Organization'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          You can upgrade and purchase seats later from the billing settings
        </p>
      </div>
    </div>
  )
}
